/** The call inspector's parts (design 11 §3.3): timeline, turns, transcript, cost, events. */
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import type { Schemas } from "../../api/client";
import { useFormat, useT, type MessageId } from "../../i18n";
import {
  STAGES, TURN_LIMIT_MS, TURN_TARGET_MS, clock, mosTone, turnTone, type Entry, type SipStep, type Source, type Stage, type Tone,
  type Turn,
} from "./timeline";

const TONE_ICON = { ok: CheckCircle2, warn: AlertTriangle, bad: AlertCircle, info: Info } as const;
const TONE_COLOR = {
  ok: "text-[var(--color-success)]",
  warn: "text-[var(--color-warning)]",
  bad: "text-[var(--color-danger)]",
  info: "text-[var(--color-muted)]",
} as const;
const TONE_WORD: Record<Tone, MessageId> = { ok: "inspect.tone.ok", warn: "inspect.tone.warn", bad: "inspect.tone.bad", info: "inspect.tone.info" };

export function Timeline({ entries }: { entries: Entry[] }) {
  const t = useT();
  return (
    <ol aria-label={t("inspect.timeline")} className="space-y-2 text-sm">
      {entries.map((e) => {
        const Icon = TONE_ICON[e.tone];
        return (
          <li key={e.id} className="flex gap-3">
            <span className="w-16 shrink-0 font-mono text-xs text-[var(--color-muted)]">{clock(e.offsetMs)}</span>
            <Icon size={16} aria-hidden="true" className={`mt-0.5 shrink-0 ${TONE_COLOR[e.tone]}`} />
            <div>
              <span className="sr-only">{t(TONE_WORD[e.tone])}: </span>
              <span className="font-medium">{"raw" in e.title ? <code>{e.title.raw}</code> : t(e.title.id, e.title.values)}</span>
              {e.details.map((d, i) => (
                <span key={i} className="block break-all text-xs text-[var(--color-muted)]">{d}</span>
              ))}
              {e.sources && <Sources sources={e.sources} />}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** The passages a search_knowledge call found, best first (E07): what the agent's answer could cite. */
function Sources({ sources }: { sources: Source[] }) {
  const t = useT();
  if (!sources.length) return <span className="block text-xs text-[var(--color-muted)]">{t("inspect.sourcesNone")}</span>;
  return (
    <div className="mt-1">
      <span className="block text-xs font-medium">{t("inspect.sources")}</span>
      <ol className="mt-1 space-y-1 text-xs">
        {sources.map((s, i) => (
          <li key={s.chunkId} className="rounded border border-[var(--color-border)] p-2">
            <span className="font-medium">{i + 1}. {s.title}</span>{" "}
            <span className="text-[var(--color-muted)]">{t("inspect.sourceScore", { score: s.score.toFixed(4) })}</span>
            <q className="mt-0.5 block text-[var(--color-muted)]">{s.snippet}</q>
          </li>
        ))}
      </ol>
    </div>
  );
}

const STAGE_LABEL: Record<Stage, MessageId> = {
  vad: "inspect.stage.vad", turn: "inspect.stage.turn", stt: "inspect.stage.stt",
  llm_ttft: "inspect.stage.llm", tool: "inspect.stage.tool", tts_ttfb: "inspect.stage.tts",
};
const STAGE_COLOR: Record<Stage, string> = {
  vad: "bg-slate-400", turn: "bg-slate-500", stt: "bg-sky-500", llm_ttft: "bg-violet-500", tool: "bg-amber-500", tts_ttfb: "bg-emerald-500",
};

/** Each turn's latency, stage by stage, on one scale; the total coloured against the targets. */
export function Turns({ turns }: { turns: Turn[] }) {
  const t = useT();
  if (!turns.length) return <p className="text-sm text-[var(--color-muted)]">{t("inspect.noTurns")}</p>;
  const scale = Math.max(TURN_LIMIT_MS, ...turns.map((x) => x.total));
  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--color-muted)]">{t("inspect.targets", { target: TURN_TARGET_MS, limit: TURN_LIMIT_MS })}</p>
      <ul className="space-y-3" aria-label={t("inspect.turns")}>
        {turns.map((turn) => {
          const tone = turnTone(turn.total);
          const Icon = TONE_ICON[tone];
          const said = turn.stages.map((s) => `${t(STAGE_LABEL[s.stage])} ${s.ms} ms`).join(", ");
          return (
            <li key={turn.turn} className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Icon size={14} aria-hidden="true" className={TONE_COLOR[tone]} />
                <span className="font-medium">{t("inspect.turn", { turn: turn.turn, ms: turn.total })}</span>
                <span className="font-mono text-xs text-[var(--color-muted)]">{clock(turn.offsetMs)}</span>
                {turn.interrupted && <span className="rounded bg-[var(--color-surface)] px-1.5 text-xs">{t("inspect.interrupted")}</span>}
              </div>
              <div className="relative h-4 rounded bg-[var(--color-surface)]" aria-hidden="true">
                <div className="flex h-full overflow-hidden rounded" style={{ width: `${Math.min(100, (turn.total / scale) * 100)}%` }}>
                  {turn.stages.map((s) => (
                    <div key={s.stage} className={STAGE_COLOR[s.stage]} style={{ width: `${(s.ms / Math.max(turn.total, 1)) * 100}%` }} title={`${s.stage} ${s.ms} ms`} />
                  ))}
                </div>
                <div className="absolute inset-y-0 w-px bg-[var(--color-text)]" style={{ left: `${(TURN_TARGET_MS / scale) * 100}%` }} />
              </div>
              <p className="text-xs text-[var(--color-muted)]">{said}</p>
            </li>
          );
        })}
      </ul>
      <ul className="flex flex-wrap gap-3 text-xs" aria-label={t("inspect.legend")}>
        {STAGES.map((s) => (
          <li key={s} className="flex items-center gap-1">
            <span className={`inline-block size-3 rounded-sm ${STAGE_COLOR[s]}`} aria-hidden="true" />
            {t(STAGE_LABEL[s])}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface Line {
  seq: number;
  role: "user" | "assistant";
  text: string;
  at: string;
  interrupted?: boolean;
}

export function Transcript({ lines, startedAt, current, onPick }: {
  lines: Line[];
  startedAt: string;
  /** The line being heard (T03's player). */
  current?: number | null;
  onPick?: (line: Line) => void;
}) {
  const t = useT();
  if (!lines.length) return <p className="text-sm text-[var(--color-muted)]">{t("inspect.noTranscript")}</p>;
  const t0 = Date.parse(startedAt);
  return (
    <ol aria-label={t("inspect.transcript")} className="space-y-2 text-sm">
      {lines.map((line) => {
        const body = (
          <>
            <span className="font-mono text-xs text-[var(--color-muted)]">{clock(Date.parse(line.at) - t0)} </span>
            <span className="font-medium">{t(line.role === "user" ? "inspect.caller" : "inspect.agent")}: </span>
            {line.text}
            {line.interrupted && <span className="ml-1 rounded bg-[var(--color-surface)] px-1.5 text-xs">{t("inspect.cutOff")}</span>}
          </>
        );
        return (
          <li key={line.seq} aria-current={current === line.seq ? "true" : undefined}
            className={`rounded px-2 py-1 ${current === line.seq ? "bg-[var(--color-surface)]" : ""}`}>
            {onPick ? (
              <button type="button" onClick={() => onPick(line)} className="w-full text-left hover:underline">{body}</button>
            ) : body}
          </li>
        );
      })}
    </ol>
  );
}

/** The SIP ladder: the far end on the left, the media node on the right, an arrow per message. */
export function Ladder({ steps }: { steps: SipStep[] }) {
  const t = useT();
  if (steps.length === 0) return <p className="text-sm text-[var(--color-muted)]">{t("inspect.noSip")}</p>;
  return (
    <div className="text-sm">
      <div aria-hidden="true" className="mb-1 flex justify-between pl-19 text-xs font-medium text-[var(--color-muted)]">
        <span>{t("inspect.sip.far")}</span>
        <span>{t("inspect.sip.node")}</span>
      </div>
      <ol aria-label={t("inspect.sip")} className="space-y-1">
        {steps.map((s) => {
          const refused = s.code !== null && s.code >= 400;
          const received = s.direction === "received";
          return (
            <li key={s.id} className="flex items-center gap-3">
              <span className="w-16 shrink-0 font-mono text-xs text-[var(--color-muted)]">{clock(s.offsetMs)}</span>
              <span className={`flex flex-1 items-center gap-1 border-x-2 border-[var(--color-border)] px-2 ${received ? "" : "flex-row-reverse"} ${refused ? TONE_COLOR.bad : ""}`}>
                <span className="sr-only">{t(received ? "inspect.sip.received" : "inspect.sip.sent")}: </span>
                <span className="font-mono text-xs font-medium">{s.message}</span>
                <span aria-hidden="true" className="h-px flex-1 bg-current opacity-60" />
                <span aria-hidden="true" className="text-xs">{received ? "▶" : "◀"}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The leg's audio at hangup: the MOS estimate beside the counts it comes from. */
export function Quality({ quality }: { quality: Schemas["Quality"] | null | undefined }) {
  const t = useT();
  const format = useFormat();
  if (!quality || quality.mos == null) return <p className="text-sm text-[var(--color-muted)]">{t("inspect.noQuality")}</p>;
  const tone = mosTone(quality.mos);
  const Icon = TONE_ICON[tone];
  const n = (v: number | null | undefined) => (v == null ? "—" : format.number(v));
  const facts: [MessageId, string][] = [
    ["inspect.codec", quality.codec ?? "—"],
    ["inspect.packetsIn", n(quality.packets_in)],
    ["inspect.packetsOut", n(quality.packets_out)],
    ["inspect.gaps", n(quality.skipped_in)],
    ["inspect.jitter", n(quality.jitter_max_variance)],
    ["inspect.qualityPct", quality.quality_pct == null ? "—" : `${quality.quality_pct}%`],
  ];
  return (
    <div className="space-y-2 text-sm">
      <p className="flex items-center gap-2">
        <Icon size={16} aria-hidden="true" className={TONE_COLOR[tone]} />
        <span className="text-lg font-semibold">{t("inspect.mos", { mos: quality.mos.toFixed(2) })}</span>
        <span className="text-[var(--color-muted)]">({t(TONE_WORD[tone])})</span>
      </p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-[var(--color-muted)]">{t(label)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-[var(--color-muted)]">{t("inspect.qualityNote")}</p>
    </div>
  );
}

const KIND_LABEL: Record<string, MessageId> = {
  telephony: "inspect.kind.telephony", stt: "inspect.kind.stt", llm_input: "inspect.kind.llmIn",
  llm_output: "inspect.kind.llmOut", tts: "inspect.kind.tts",
};

export function Cost({ cost, usage }: { cost: Schemas["Cost"] | null | undefined; usage: Schemas["UsageItem"][] | null | undefined }) {
  const t = useT();
  const format = useFormat();
  if (!cost) return <p className="text-sm text-[var(--color-muted)]">{t("inspect.noCost")}</p>;
  const quantity = (kind: string, provider: string) =>
    usage?.find((u) => u.kind === kind && u.provider === provider);
  return (
    <div className="space-y-2 text-sm">
      <p>
        <span className="text-lg font-semibold">{t("calls.cost", { amount: format.money(cost.total, cost.currency) })}</span>{" "}
        <span className="text-xs text-[var(--color-muted)]">{t("inspect.estimate", { prices: cost.prices })}</span>
      </p>
      <table className="w-full text-left text-xs">
        <caption className="sr-only">{t("inspect.costSplit")}</caption>
        <thead className="text-[var(--color-muted)]">
          <tr><th scope="col">{t("inspect.kind")}</th><th scope="col">{t("builder.provider")}</th><th scope="col">{t("inspect.quantity")}</th><th scope="col" className="text-right">{t("calls.col.cost")}</th></tr>
        </thead>
        <tbody>
          {cost.items.map((item, i) => {
            const u = quantity(item.kind, item.provider);
            return (
              <tr key={i} className="border-t border-[var(--color-border)]">
                <td>{KIND_LABEL[item.kind] ? t(KIND_LABEL[item.kind] as MessageId) : item.kind}</td>
                <td>{item.provider}{item.model ? ` · ${item.model}` : ""}</td>
                <td>{u ? `${u.quantity} ${u.unit}` : "—"}</td>
                <td className="text-right">{item.priced ? format.money(item.amount, cost.currency) : t("inspect.unpriced")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
